import { Button } from 'primereact/button';
import { Trans, useTranslation } from 'react-i18next';

import tvFrameImage from '@features/campaigns/builder/assets/images/creative-preview-tv-frame-opt.png';
import tvProgramBorderGuardImageDE from '@features/campaigns/builder/assets/images/tv-program-de-border-guard.png';
import tvProgramPlaceholderImageDE from '@features/campaigns/builder/assets/images/tv-program-de-placeholder.jpg';
import { SwitchInPreviewModalOptions } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { InfoPanel } from '@shared/components/InfoPanel/InfoPanel.tsx';
import { InfoPanelTypes } from '@shared/components/InfoPanel/InfoPanel.types.ts';
import { LinkText } from '@shared/components/LinkText/LinkText';
import { i18nNameSpace } from '@shared/consts/i18n.ts';

import styles from './PreviewSwitchInWrapper.module.scss';

interface PreviewSwitchInWrapperProps {
  leftCreativeUrl?: string;
  bottomCreativeUrl?: string;
  isLeftPreviewEditable: boolean;
  isBottomPreviewEditable: boolean;
  options?: SwitchInPreviewModalOptions;
}

const SWITCH_IN_INSTRUCTIONS_DE_URL = 'https://admanager.prosiebensat1.com/de/lanner/';

export function PreviewSwitchInWrapper({
  leftCreativeUrl,
  bottomCreativeUrl,
  isLeftPreviewEditable,
  isBottomPreviewEditable,
  options,
}: PreviewSwitchInWrapperProps) {
  const { t } = useTranslation([i18nNameSpace.CAMPAIGNS]);
  const hasLeftCreative = Boolean(leftCreativeUrl);
  const hasBottomCreative = Boolean(bottomCreativeUrl);
  const canRejectLeftCreative = hasLeftCreative && isLeftPreviewEditable;
  const canRejectBottomCreative = hasBottomCreative && isBottomPreviewEditable;
  const shouldShowFooter = canRejectLeftCreative || canRejectBottomCreative;

  return (
    <div className={styles.container}>
      {shouldShowFooter && (
        <div className={styles.contentContainer}>
          <InfoPanel type={InfoPanelTypes.INFO} headerText={t('builder.switchInPreview.header')}>
            <ul>
              <li key={'hint01'}>
                <Trans
                  t={t}
                  i18nKey={'builder.switchInPreview.content.hint01'}
                  components={[<LinkText externalLink key={0} to={SWITCH_IN_INSTRUCTIONS_DE_URL} />]}
                />
              </li>
              <li key={'hint02'}>{t('builder.switchInPreview.content.hint02')}</li>
              <li key={'hint03'}>{t('builder.switchInPreview.content.hint03')}</li>
              <li key={'hint04'}>{t('builder.switchInPreview.content.hint04')}</li>
            </ul>
          </InfoPanel>
        </div>
      )}

      <div className={styles.tvContainer}>
        <img src={tvFrameImage} alt={'tv-frame'} className={styles.tvFrameBackground} />
        <div className={styles.creativeCheckeredBg}>
          <img
            src={tvProgramBorderGuardImageDE}
            alt={'tv-program-broadcast-border-guard'}
            className={styles.broadcastBorder}
          />
          <img
            src={tvProgramPlaceholderImageDE}
            alt={'tv-program-placeholder'}
            className={styles.tvProgramPlaceholder}
          />
          <div className={styles.creativePreviewBox}>
            {hasLeftCreative && (
              <img src={leftCreativeUrl} alt={'switch-in-left-creative-preview'} className={styles.leftCreative} />
            )}
            {hasBottomCreative && (
              <img
                src={bottomCreativeUrl}
                alt={'switch-in-bottom-creative-preview'}
                className={styles.bottomCreative}
              />
            )}
          </div>
        </div>
      </div>

      {shouldShowFooter && (
        <div className={styles.footer}>
          {canRejectLeftCreative && (
            <Button
              data-testid='switch-in-preview-reject-left-cta-button'
              className={styles.editButton}
              icon='pi pi-trash'
              onClick={options?.rejectLeftImageCallback}
              text
              severity='danger'
              type='button'
            >
              {t('builder.switchInPreview.footer.deleteLeftImage')}
            </Button>
          )}
          {canRejectBottomCreative && (
            <Button
              data-testid='switch-in-preview-reject-bottom-cta-button'
              className={styles.editButton}
              icon='pi pi-trash'
              onClick={options?.rejectBottomImageCallback}
              text
              severity='danger'
              type='button'
            >
              {t('builder.switchInPreview.footer.deleteBottomImage')}
            </Button>
          )}
          <Button
            data-testid='switch-in-preview-accept-cta-button'
            className={styles.deleteButton}
            onClick={options?.acceptImageCallback}
            type='button'
          >
            {t('builder.modals.ok')}
          </Button>
        </div>
      )}
    </div>
  );
}
